import { useEffect, useState, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, MapPin, Flag, Lock, Repeat } from "lucide-react";
import { api, errMsg, money, fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StatusBadge, TruckBadge } from "@/components/Badges";
import { Stars } from "@/components/Rating";
import { ShipperBids, TransporterBidBox } from "@/components/BidPanels";
import { t } from "@/lib/i18n";

const Stat = ({ label, value, testId }) => (
  <div>
    <p className="font-mono text-[10px] uppercase tracking-wider text-slate-500">{label}</p>
    <p className="font-semibold mt-1 break-words" data-testid={testId}>{value}</p>
  </div>
);

export default function LoadDetail() {
  const { id } = useParams();
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [load, setLoad] = useState(null);

  const reload = useCallback(() => api.get(`/loads/${id}`).then((r) => setLoad(r.data)).catch((e) => toast.error(errMsg(e))), [id]);
  useEffect(() => { reload(); }, [reload]);

  if (!load) return <p className="font-mono text-sm text-slate-500">{t("LOADING…")}</p>;
  const cur = load.currency || "USD";

  const cancel = async () => {
    try {
      const { data } = await api.post(`/loads/${id}/cancel`);
      toast.success(data.refunded ? t("Load cancelled — 1 credit refunded (no bids received)") : t("Load cancelled"));
      refresh();
      reload();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="grid lg:grid-cols-12 gap-6">
      <div className="lg:col-span-7 space-y-6 min-w-0">
        <div className="hf-card p-6 hf-rise">
          <div className="flex flex-wrap items-center gap-2">
            <StatusBadge status={load.status} testId="load-status" />
            <TruckBadge type={load.truck_type} />
            <span className="font-mono text-xs text-slate-500">{load.shipper_code}</span>
            <Stars rating={load.shipper_rating} testId="load-shipper-rating" />
          </div>
          <h1 className="font-display font-black text-2xl sm:text-3xl mt-4 flex flex-wrap items-center gap-2" data-testid="load-corridor">
            {load.loading_area} <ArrowRight className="w-5 h-5 text-amber-600" /> {load.offloading_area}
          </h1>
          <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-5 border-t pt-6">
            <Stat label={t("Commodity")} value={t(load.commodity)} />
            <Stat label={t("Tonnage")} value={`${load.tonnage} t`} />
            <Stat label={t("Rate p/mt")} value={money(load.price_per_mt, cur)} testId="load-price" />
            <Stat label={t("Est. total")} value={money(load.price_per_mt * load.tonnage, cur)} />
            <Stat label={t("Start date")} value={fmtDate(load.loading_date)} />
            <Stat label={t("Completion date")} value={fmtDate(load.offloading_date)} />
            {load.agreed_price_per_mt && <Stat label={t("Agreed p/mt")} value={money(load.agreed_price_per_mt, cur)} />}
          </div>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          {[["Loading point", MapPin, load.loading_area, load.loading_address, load.loading_region, load.loading_country], ["Offloading point", Flag, load.offloading_area, load.offloading_address, load.offloading_region, load.offloading_country]].map(([lbl, Icon, area, addr, region, country]) => (
            <div key={lbl} className="hf-card p-5">
              <p className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-wider text-slate-500"><Icon className="w-3.5 h-3.5 text-amber-600" />{t(lbl)}</p>
              <p className="font-semibold mt-2">{area}</p>
              {(region || country) && <p className="text-xs font-mono text-slate-500">{[region, country].filter(Boolean).join(", ")}</p>}
              <p className="text-sm text-slate-600 break-words">{addr}</p>
            </div>
          ))}
        </div>
        {load.notes && <div className="hf-card p-5"><p className="hf-label">{t("Notes")}</p><p className="text-sm whitespace-pre-wrap">{load.notes}</p></div>}
        <div className="flex items-center gap-2 text-xs text-slate-500"><Lock className="w-3.5 h-3.5" /> {t("Company identity and contact details are shared only after both parties confirm.")}</div>
      </div>
      <div className="lg:col-span-5 space-y-4">
        {user.role === "shipper" && <ShipperBids load={load} onChange={reload} />}
        {user.role === "shipper" && load.status === "open" && (
          <button onClick={cancel} className="hf-btn-danger w-full" data-testid="btn-cancel-load">
            Cancel load{(load.bids || []).length === 0 ? " · credit refunded" : ""}
          </button>
        )}
        {user.role === "shipper" && load.status !== "open" && (
          <button onClick={() => navigate("/post-load", { state: { repost: load } })} className="hf-btn-primary w-full" data-testid="btn-repost-load"><Repeat className="w-4 h-4" /> {t("Repost this load · 1 credit")}</button>
        )}
        {user.role === "transporter" && <TransporterBidBox load={load} onChange={reload} />}
        {load.matched_bid_id && user.role !== "admin" && (
          <Link to={`/connections/${load.matched_bid_id}`} className="hf-btn-dark w-full" data-testid="btn-open-connection">{t("Open connection & chat")}</Link>
        )}
      </div>
    </div>
  );
}
